
import imageOptimizer from '../utils/ImageOptimizer.js';
import { logger } from '@/utils/logger.js';
import { useTokchanStore } from '@/store/tokchanStore.js';

export interface FallbackSystemOptions {
    retryAttempts?: number;
    loadingTimeout?: number;
    retryDelay?: number;
    productionMode?: boolean;
}

class AssetFallbackSystem {
    private productionMode: boolean;
    private fallbackAssets: Map<string, string> = new Map();
    private processedElements: WeakSet<HTMLElement> = new WeakSet();
    private processedUrls: Set<string> = new Set();
    private processingInProgress: Set<string> = new Set();
    private serverConnectionIssues: number = 0;
    private fallbackMode: boolean = false;
    private failedAssets: { src: string; type: string; timestamp: number }[] = [];

    constructor(options: { productionMode?: boolean } = {}) {
        this.productionMode = options.productionMode ?? false;


        this.initializeDefaultFallbacks();
        this.setupGlobalErrorHandlers();
        this.setupEmptyFileDetection();
        this.preloadCriticalImages();
    }

    private initializeDefaultFallbacks(): void {
        this.fallbackAssets.set('eyes', this.generateEyeFallback());
        this.fallbackAssets.set('mouth', this.generateMouthFallback());
        this.fallbackAssets.set('body', this.generateBodyFallback());
        this.fallbackAssets.set('background', this.generateBackgroundFallback());
    }

    private setupGlobalErrorHandlers(): void {
        document.addEventListener('error', (event) => {
            const target = event.target as HTMLElement;
            if (!target) return;

            try {
                if (target.tagName === 'IMG') {
                    this.handleImageError(target as HTMLImageElement);
                } else if (target.tagName === 'VIDEO') {
                    this.handleVideoError(target as HTMLVideoElement);
                }
            } catch (error) {
                logger.error('Error in global error handler:', error);
            }
        }, true);
    }

    private setupEmptyFileDetection(): void {
        document.addEventListener('loadstart', (event) => {
            const target = event.target as HTMLVideoElement;
            if (target && target.tagName === 'VIDEO') {
                this.checkVideoFileSize(target);
            }
        }, true);
    }

    private async checkVideoFileSize(videoElement: HTMLVideoElement): Promise<boolean> {
        if (!videoElement || !videoElement.src) return true;
        const { src } = videoElement;

        if (src.startsWith('data:') || src.includes('/effect/') || src.includes('/backgrounds/')) {
            return true;
        }

        if (this.processedUrls.has(src + '_empty_check')) {
            return true;
        }

        try {
            const controller = new AbortController();
            const timeoutId = setTimeout(() => controller.abort(), 8000);
            const response = await fetch(src, { method: 'HEAD', signal: controller.signal });
            clearTimeout(timeoutId);

            const contentLength = response.headers.get('content-length');
            const fileSize = contentLength ? parseInt(contentLength, 10) : 0;

            if (fileSize < 1000) { // 1KB未満
                logger.warn(`Empty or corrupted video file detected: ${src} (${fileSize} bytes)`);
                this.processedUrls.add(src + '_empty_check');
                this.handleVideoError(videoElement);
                videoElement.src = '';
                return false;
            }
            this.processedUrls.add(src + '_empty_check');
            return true;
        } catch (error: unknown) {
            if (error instanceof Error && error.name !== 'AbortError') {
                logger.warn(`Failed to check video file size: ${src}`, error.message);
            }
            return true;
        }
    }

    private async handleImageError(imgElement: HTMLImageElement): Promise<void> {
        if (!imgElement || this.processedElements.has(imgElement)) return;

        const originalSrc = imgElement.src || 'unknown';
        if (this.productionMode || this.processingInProgress.has(originalSrc) || originalSrc.startsWith('data:')) {
            return;
        }

        this.processingInProgress.add(originalSrc);
        this.processedElements.add(imgElement);

        if (originalSrc.includes('localhost')) {
            this.serverConnectionIssues++;
            this.checkServerStatus();
        }

        if (!this.processedUrls.has(originalSrc)) {
            logger.warn(`Image load failed: ${originalSrc}`);
        }

        if (imgElement.dataset.fallbackApplied === 'true') {
            this.processingInProgress.delete(originalSrc);
            return;
        }

        const fallbackKey = this.getFallbackKey(originalSrc);

        if (this.fallbackMode) {
            this.applyFallback(imgElement, originalSrc, fallbackKey);
            this.processedUrls.add(originalSrc);
            this.processingInProgress.delete(originalSrc);
            this.trackFailedAsset(originalSrc, fallbackKey);
            return;
        }

        this.applyFallback(imgElement, originalSrc, fallbackKey);
        this.processedUrls.add(originalSrc);
        this.processingInProgress.delete(originalSrc);
        this.trackFailedAsset(originalSrc, fallbackKey);
    }

    private applyFallback(imgElement: HTMLImageElement, originalSrc: string, fallbackKey: string): void {
        const fallbackSrc = this.fallbackAssets.get(fallbackKey) || this.generateGenericFallback();
        imgElement.src = fallbackSrc;
        imgElement.dataset.fallbackApplied = 'true';
        imgElement.dataset.originalSrc = originalSrc;
    }

    private handleVideoError(videoElement: HTMLVideoElement): void {
        if (!videoElement || this.processedElements.has(videoElement)) return;

        const originalSrc = videoElement.src || 'unknown';
        if (this.processingInProgress.has(originalSrc) || originalSrc.startsWith('data:')) {
            return;
        }

        this.processingInProgress.add(originalSrc);
        this.processedElements.add(videoElement);

        if (videoElement.dataset.fallbackApplied === 'true') {
            this.processingInProgress.delete(originalSrc);
            return;
        }

        if (originalSrc.includes('/transition/')) {
            videoElement.style.display = 'none';
            videoElement.pause();
            // Notify RandomBackgroundManager or similar system (Zustand store経由)
            const bgManager = useTokchanStore.getState().getRandomBackgroundManager();
            if (bgManager?.handleTransitionFailure) {
                setTimeout(() => bgManager.handleTransitionFailure(new Error('Video load failed')), 100);
            }
        } else if (videoElement.id.includes('background-video')) {
            if (!videoElement.parentNode) return;
            const fallbackImg = document.createElement('img');
            fallbackImg.src = this.fallbackAssets.get('background')!;
            fallbackImg.style.cssText = videoElement.style.cssText;
            fallbackImg.style.objectFit = 'cover';
            videoElement.parentNode.replaceChild(fallbackImg, videoElement);
        }

        videoElement.dataset.fallbackApplied = 'true';
        this.processedUrls.add(originalSrc);
        this.processingInProgress.delete(originalSrc);
        this.trackFailedAsset(originalSrc, 'video');
    }

    private getFallbackKey(src: string): string {
        if (src.includes('eyes/')) return 'eyes';
        if (src.includes('mouth/')) return 'mouth';
        if (src.includes('body/')) return 'body';
        return 'generic';
    }

    private checkServerStatus(): void {
        if (this.serverConnectionIssues >= 3 && !this.fallbackMode) {
            logger.warn('Multiple connection issues detected - enabling fallback mode');
            this.fallbackMode = true;
        }
    }

    private preloadCriticalImages(): void {
        const criticalImages = [
            '/assets/characters/eyes/eyes_1.png',
            '/assets/characters/mouth/mouth_1.png',
            '/assets/characters/body/body_1.png'
        ];
        criticalImages.forEach(src => imageOptimizer.preloadImage(src));
    }

    private trackFailedAsset(src: string, type: string): void {
        if (!src || !type) return;
        this.failedAssets.push({ src, type, timestamp: Date.now() });
        if (this.failedAssets.length > 10) {
            logger.warn('High number of failed assets detected - check network or deployment');
        }
    }

    private generateEyeFallback(): string {
        const svg = `<svg width="100" height="60" xmlns="http://www.w3.org/2000/svg"><ellipse cx="25" cy="30" rx="15" ry="20" fill="#f0f0f0" stroke="#333" stroke-width="2"/><circle cx="25" cy="28" r="8" fill="#333"/><circle cx="27" cy="26" r="3" fill="#fff"/><ellipse cx="75" cy="30" rx="15" ry="20" fill="#f0f0f0" stroke="#333" stroke-width="2"/><circle cx="75" cy="28" r="8" fill="#333"/><circle cx="77" cy="26" r="3" fill="#fff"/></svg>`;
        return `data:image/svg+xml,${encodeURIComponent(svg)}`;
    }

    private generateMouthFallback(): string {
        const svg = `<svg width="80" height="40" xmlns="http://www.w3.org/2000/svg"><path d="M20 25 Q40 15 60 25" stroke="#333" stroke-width="3" fill="none" stroke-linecap="round"/></svg>`;
        return `data:image/svg+xml,${encodeURIComponent(svg)}`;
    }

    private generateBodyFallback(): string {
        const svg = `<svg width="200" height="300" xmlns="http://www.w3.org/2000/svg"><ellipse cx="100" cy="150" rx="80" ry="120" fill="#ffeb3b" stroke="#333" stroke-width="2"/><circle cx="85" cy="100" r="5" fill="#333"/><circle cx="115" cy="100" r="5" fill="#333"/><path d="M85 120 Q100 130 115 120" stroke="#333" stroke-width="2" fill="none"/></svg>`;
        return `data:image/svg+xml,${encodeURIComponent(svg)}`;
    }

    private generateBackgroundFallback(): string {
        const svg = `<svg width="800" height="600" xmlns="http://www.w3.org/2000/svg"><rect width="800" height="600" fill="#ff00ff"/><text x="400" y="300" text-anchor="middle" fill="#fff" font-size="24">Background Unavailable</text></svg>`;
        return `data:image/svg+xml,${encodeURIComponent(svg)}`;
    }

    private generateGenericFallback(): string {
        const svg = `<svg width="100" height="100" xmlns="http://www.w3.org/2000/svg"><rect width="100" height="100" fill="#f0f0f0" stroke="#ccc" stroke-width="2"/><text x="50" y="50" text-anchor="middle" dominant-baseline="middle" fill="#999" font-size="12">No Image</text></svg>`;
        return `data:image/svg+xml,${encodeURIComponent(svg)}`;
    }
}

export default AssetFallbackSystem;
